import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect } from 'react'
import { getAllComplaintsByPGID } from '../apis/services/pgApi'

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const TYPE_ICON = {
  'Water Leakage':'🚿','WiFi Issue':'📶','AC Not Working':'❄️','Noisy Neighbours':'🔊',
  'Housekeeping':'🧹','Door Lock':'🔒','Plumbing':'🔧','Electricity':'⚡','Security':'🛡️',
  'Pest Control':'🪲','Furniture':'🪑','Geyser/Hot Water':'🚿','Fan':'💨',
  'Drainage':'🚰','Paint/Walls':'🎨','Cleanliness':'🧼','Laundry':'👕','Parking':'🚗',
  'Noise':'📢','CCTV/Safety':'📷','Lift/Elevator':'🛗','Gas/Cooking':'🔥',
  'Window/Door':'🪟','Mattress/Bedding':'🛏️','Food Quality':'🍽️',
}

const HIGH_PRI   = ['Water Leakage','Plumbing','Electricity','AC Not Working','Door Lock','Geyser/Hot Water','Security','Gas/Cooking','Lift/Elevator','CCTV/Safety']
const MOD_PRI    = ['WiFi Issue','Housekeeping','Furniture','Pest Control','Window/Door','Drainage','Paint/Walls']

function getPriority(type) {
  if (HIGH_PRI.includes(type)) return 'high'
  if (MOD_PRI.includes(type))  return 'moderate'
  return 'low'
}

function mapStatus(s) {
  const l = (s || '').toLowerCase()
  if (l === 'resolved') return 'resolved'
  if (l === 'rejected') return 'rejected'
  return 'open'
}

function formatDate(str) {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Shared components ──────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

function PriBadge({ type }) {
  const p = getPriority(type)
  const cls = { high: 'cp-rpt-pri-high', moderate: 'cp-rpt-pri-mod', low: 'cp-rpt-pri-low' }[p]
  const lbl = { high: 'High', moderate: 'Moderate', low: 'Low' }[p]
  return <span className={`cp-rpt-pri ${cls}`}>{lbl}</span>
}

function StatusBadge({ status }) {
  const s   = mapStatus(status)
  const cls = { open: 'cp-rpt-st-open', resolved: 'cp-rpt-st-res', rejected: 'cp-rpt-st-rej' }[s]
  const lbl = { open: 'Open', resolved: 'Resolved', rejected: 'Rejected' }[s]
  const dot = { open: '#3B82F6', resolved: '#22C55E', rejected: '#EF4444' }[s]
  return (
    <span className={`cp-rpt-status ${cls}`}>
      <span className="cp-rpt-dot" style={{ background: dot }} />{lbl}
    </span>
  )
}

// ─── Simple dummy data ──────────────────────────────────────────────────────

const getDummyComplaints = () => {
  const types = [
    'WiFi Issue', 'Housekeeping', 'AC Not Working', 'Water Leakage', 'Plumbing',
    'Noisy Neighbours', 'Electricity', 'Fan', 'Cleanliness', 'Pest Control',
    'Furniture', 'Door Lock', 'Geyser/Hot Water', 'Drainage', 'Laundry'
  ]
  
  const tenants = [
    'Rahul Sharma', 'Sneha Reddy', 'Arjun Choudhary', 'Pooja Gupta', 'Meena Dasari',
    'Vikas Rao', 'Kiran Menon', 'Deepak Joshi', 'Ananya Singh', 'Suresh Iyer'
  ]
  
  const rooms = ['101', '102', '103', '201', '202', '203', '301', '302', '303']
  const statuses = ['Open', 'RESOLVED', 'Open', 'RESOLVED', 'Open', 'REJECTED']
  
  const complaints = []
  let id = 1
  
  // Generate complaints for 2023-2026
  for (let year = 2023; year <= 2026; year++) {
    const maxMonth = year === 2026 ? 5 : 11 // 2026 only until June
    const numPerMonth = year === 2023 ? 8 : year === 2024 ? 10 : year === 2025 ? 12 : 14
    
    for (let month = 0; month <= maxMonth; month++) {
      const count = Math.floor(numPerMonth * (0.7 + Math.random() * 0.6))
      
      for (let i = 0; i < count; i++) {
        const type = types[Math.floor(Math.random() * types.length)]
        const day = Math.floor(Math.random() * 28) + 1
        const status = statuses[Math.floor(Math.random() * statuses.length)]
        
        complaints.push({
          ComplaintID: id++,
          ComplaintText: type,
          ComplaintStatus: status,
          ComplaintDate: new Date(year, month, day).toISOString(),
          tenantName: tenants[Math.floor(Math.random() * tenants.length)],
          RoomNumber: rooms[Math.floor(Math.random() * rooms.length)],
          _year: String(year),
          _month: month
        })
      }
    }
  }
  
  return complaints
}

// ─── Overview tab ──────────────────────────────────────────────────────────

function CpOverview({ data, selectedYear }) {
  const all = Array.isArray(data) ? data : []
  const open = all.filter(c => mapStatus(c.ComplaintStatus) === 'open')
  const resolved = all.filter(c => mapStatus(c.ComplaintStatus) === 'resolved')
  const highOpen = open.filter(c => getPriority(c.ComplaintText) === 'high')
  const resRate = all.length ? Math.round((resolved.length / all.length) * 100) : 0

  let verdict, verdictCls
  if (highOpen.length === 0 && open.length < 5) {
    verdict = `Your PG is in good shape — only ${open.length} open complaint${open.length !== 1 ? 's' : ''} and no high-priority issues pending.`
    verdictCls = 'cp-rpt-verdict-good'
  } else if (highOpen.length > 0) {
    verdict = `${highOpen.length} high-priority complaint${highOpen.length !== 1 ? 's are' : ' is'} open — things like plumbing, electricity, or water issues tenants expect fixed fast.`
    verdictCls = 'cp-rpt-verdict-bad'
  } else {
    verdict = `${open.length} complaints are open. None are high priority, but clearing the backlog keeps tenants happy.`
    verdictCls = 'cp-rpt-verdict-ok'
  }

  const typeCounts = {}
  all.forEach(c => { typeCounts[c.ComplaintText] = (typeCounts[c.ComplaintText] || 0) + 1 })
  const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 7)
  const maxCount = topTypes[0]?.[1] || 1

  const monthlyVol = MONTHS.map((_, mi) => all.filter(c => c._year === selectedYear && c._month === mi).length)

  const priColor = { high: '#EF4444', moderate: '#F59E0B', low: '#22C55E' }

  return (
    <div className="cp-rpt-section">

      <div className="rpt-tiles">
        <StatTile label="Open complaints" value={open.length} sub={highOpen.length > 0 ? `${highOpen.length} high priority` : 'None high priority'} accent="#EF4444" />
        <StatTile label="Resolution rate" value={`${resRate}%`} sub={`${resolved.length} of ${all.length} resolved`} accent="#22C55E" />
        <StatTile label="Total complaints" value={all.length} sub="All time" accent="#6366F1" />
        <StatTile label="High priority open" value={highOpen.length} sub="Needs immediate action" accent="#F59E0B" />
      </div>

      <div className={`cp-rpt-verdict-card ${verdictCls}`}>
        <p className="cp-rpt-verdict-text">{verdict}</p>
        <div className="cp-rpt-verdict-pills">
          <span className="cp-rpt-vpill cp-rpt-vpill-red">
            <span>🔴</span><span className="vpl">High priority open</span><strong>{highOpen.length}</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-green">
            <span>✓</span><span className="vpl">Resolution rate</span><strong>{resRate}%</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-blue">
            <span>📅</span><span className="vpl">This year ({selectedYear})</span>
            <strong>{all.filter(c => c._year === selectedYear).length} total</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-gray">
            <span>📌</span><span className="vpl">Most reported issue</span><strong>{topTypes[0]?.[0] || '—'}</strong>
          </span>
        </div>
      </div>

      <div className="cp-rpt-two-col">

        <div className="cp-rpt-card">
          <div className="cp-rpt-card-title">Top recurring issues <span className="cp-rpt-card-sub">all time</span></div>
          <div className="cp-rpt-issue-rows">
            {topTypes.map(([type, count]) => {
              const pri = getPriority(type)
              const barW = Math.round((count / maxCount) * 100)
              return (
                <div key={type} className="cp-rpt-issue-row">
                  <span className="cp-rpt-issue-icon">{TYPE_ICON[type] || '📌'}</span>
                  <span className="cp-rpt-issue-name">{type}</span>
                  <div className="cp-rpt-issue-track">
                    <div className="cp-rpt-issue-bar" style={{ width: `${barW}%`, background: priColor[pri] }} />
                  </div>
                  <span className="cp-rpt-issue-count">{count}</span>
                  <PriBadge type={type} />
                </div>
              )
            })}
          </div>
          <p className="cp-rpt-card-hint">Bar length = frequency · colour = priority level</p>
        </div>

        <div className="cp-rpt-card">
          <div className="cp-rpt-card-title">Monthly volume <span className="cp-rpt-card-sub">{selectedYear}</span></div>
          <div className="cp-rpt-sparkbars">
            {monthlyVol.map((n, i) => {
              const maxV = Math.max(...monthlyVol, 1)
              const h = Math.max(4, Math.round((n / maxV) * 100))
              return (
                <div key={i} className="cp-rpt-spark-col">
                  <span className="cp-rpt-spark-val">{n}</span>
                  <div className="cp-rpt-spark-bar" style={{ height: `${h}%` }} />
                  <span className="cp-rpt-spark-lbl">{MONTHS[i]}</span>
                </div>
              )
            })}
          </div>
          <p className="cp-rpt-card-hint">Number of complaints raised each month</p>
        </div>

      </div>
    </div>
  )
}

// ─── Monthly trends tab ────────────────────────────────────────────────────

function CpTrends({ data }) {
  const all = Array.isArray(data) ? data : []
  const years = ['2023','2024','2025','2026'].filter(y => all.some(c => c._year === y))
  const yearColors = { '2023':'#B4B2A9','2024':'#85B7EB','2025':'#F06E18','2026':'#22C55E' }

  const mthRows = MONTHS.map((m, mi) => {
    const vals = years.map(y => all.filter(c => c._year === y && c._month === mi).length)
    const last = vals[vals.length - 1]
    const prev = vals[vals.length - 2]
    const diff = prev !== undefined ? last - prev : null
    return { month: m, vals, diff }
  })

  const totals = years.map(y => all.filter(c => c._year === y).length)
  const resRates = years.map(y => {
    const yc = all.filter(c => c._year === y)
    return yc.length ? Math.round((yc.filter(c => mapStatus(c.ComplaintStatus) === 'resolved').length / yc.length) * 100) : 0
  })

  const latestY = years[years.length - 1]
  const prevY = years[years.length - 2]
  const totalDiff = prevY ? totals[years.length - 1] - totals[years.length - 2] : null

  return (
    <div className="cp-rpt-section">

      <div className="cp-rpt-trends-banner">
        <div className="cp-rpt-tbanner-item">
          <span className="cp-rpt-tbanner-lbl">Total in {latestY}</span>
          <span className="cp-rpt-tbanner-val">{totals[years.length - 1]}</span>
        </div>
        {totalDiff !== null && (
          <div className="cp-rpt-tbanner-item">
            <span className="cp-rpt-tbanner-lbl">vs {prevY}</span>
            <span className={`cp-rpt-tbanner-val ${totalDiff > 0 ? 'cp-rpt-up' : 'cp-rpt-dn'}`}>
              {totalDiff > 0 ? `+${totalDiff}` : totalDiff} complaints
            </span>
          </div>
        )}
        <div className="cp-rpt-tbanner-item">
          <span className="cp-rpt-tbanner-lbl">Resolution rate {latestY}</span>
          <span className="cp-rpt-tbanner-val cp-rpt-dn-good">{resRates[years.length - 1]}%</span>
        </div>
      </div>

      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>Month-by-month breakdown</span>
          <div className="cp-rpt-yr-legend">
            {years.map(y => (
              <span key={y} className="cp-rpt-yr-leg-item">
                <span className="cp-rpt-yr-dot" style={{ background: yearColors[y] }} />{y}
              </span>
            ))}
          </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th>Month</th>
                {years.map(y => <th key={y} style={{ color: yearColors[y] }}>{y}</th>)}
                <th>{prevY}→{latestY}</th>
              </tr>
            </thead>
            <tbody>
              {mthRows.map((row, i) => (
                <tr key={i}>
                  <td className="cp-rpt-td-bold">{row.month}</td>
                  {row.vals.map((v, vi) => <td key={vi}>{v}</td>)}
                  <td>
                    {row.diff === null ? '—'
                      : row.diff > 0
                        ? <span className="cp-rpt-diff-up">+{row.diff}</span>
                        : row.diff < 0
                          ? <span className="cp-rpt-diff-dn">{row.diff}</span>
                          : <span className="cp-rpt-diff-eq">—</span>
                    }
                  </td>
                </tr>
              ))}
              <tr className="cp-rpt-total-row">
                <td className="cp-rpt-td-bold">Total</td>
                {totals.map((t, i) => <td key={i}><strong>{t}</strong></td>)}
                <td>
                  {totalDiff !== null
                    ? totalDiff > 0
                      ? <span className="cp-rpt-diff-up">+{totalDiff}</span>
                      : <span className="cp-rpt-diff-dn">{totalDiff}</span>
                    : '—'
                  }
                </td>
              </tr>
              <tr>
                <td className="cp-rpt-td-bold">Resolved %</td>
                {resRates.map((r, i) => (
                  <td key={i}>
                    {r}%
                    <span className="cp-rpt-res-bar">
                      <span className="cp-rpt-res-fill" style={{ width: `${r}%` }} />
                    </span>
                  </td>
                ))}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  )
}

// ─── All complaints tab ────────────────────────────────────────────────────

function CpAllComplaints({ data }) {
  const all = Array.isArray(data) ? data : []
  const [statusF, setStatusF] = useState('all')
  const [priF, setPriF] = useState('all')
  const [sortCol, setSortCol] = useState('date')
  const [sortDir, setSortDir] = useState('desc')

  const filtered = all.filter(c => {
    const s = mapStatus(c.ComplaintStatus)
    const p = getPriority(c.ComplaintText)
    return (statusF === 'all' || s === statusF) && (priF === 'all' || p === priF)
  })

  const sorted = [...filtered].sort((a, b) => {
    let av, bv
    if (sortCol === 'date') { av = new Date(a.ComplaintDate); bv = new Date(b.ComplaintDate) }
    if (sortCol === 'type') { av = a.ComplaintText; bv = b.ComplaintText }
    if (sortCol === 'room') { av = a.RoomNumber; bv = b.RoomNumber }
    if (sortCol === 'tenant') { av = a.tenantName; bv = b.tenantName }
    if (sortCol === 'status') { av = mapStatus(a.ComplaintStatus); bv = mapStatus(b.ComplaintStatus) }
    if (sortCol === 'pri') { av = getPriority(a.ComplaintText); bv = getPriority(b.ComplaintText) }
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const SI = ({ col }) => (
    <span className={`cp-rpt-si${sortCol === col ? ' cp-rpt-si-on' : ''}`}>
      {sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
    </span>
  )

  return (
    <div className="cp-rpt-section">
      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>All complaints</span>
          <div className="cp-rpt-filter-row">
            <div className="cp-rpt-filter-group">
              {['all','open','resolved'].map(f => (
                <button key={f} className={`cp-rpt-fpill${statusF === f ? ' on' : ''}`} onClick={() => setStatusF(f)}>
                  {f === 'all' ? 'All status' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className="cp-rpt-filter-group">
              {['all','high','moderate','low'].map(f => (
                <button key={f} className={`cp-rpt-fpill${priF === f ? ' on' : ''}`} onClick={() => setPriF(f)}>
                  {f === 'all' ? 'All priority' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('type')}>Issue<SI col="type" /></th>
                <th onClick={() => handleSort('room')}>Room<SI col="room" /></th>
                <th onClick={() => handleSort('tenant')}>Tenant<SI col="tenant" /></th>
                <th onClick={() => handleSort('pri')}>Priority<SI col="pri" /></th>
                <th onClick={() => handleSort('date')}>Date<SI col="date" /></th>
                <th onClick={() => handleSort('status')}>Status<SI col="status" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={6} className="cp-rpt-empty-row">No complaints match the selected filters.</td></tr>
              ) : sorted.map((c, i) => (
                <tr key={c.ComplaintID || i}>
                  <td>
                    <span className="cp-rpt-type-cell">
                      <span className="cp-rpt-type-icon">{TYPE_ICON[c.ComplaintText] || '📌'}</span>
                      {c.ComplaintText}
                    </span>
                  </td>
                  <td><span className="cp-rpt-chip">{c.RoomNumber}</span></td>
                  <td>{c.tenantName}</td>
                  <td><PriBadge type={c.ComplaintText} /></td>
                  <td className="cp-rpt-td-mono">{formatDate(c.ComplaintDate)}</td>
                  <td><StatusBadge status={c.ComplaintStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cp-rpt-tbl-foot">{sorted.length} record{sorted.length !== 1 ? 's' : ''} · {all.length} total</div>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const OwnerComplaintsReport = ({ selectedPGId, selectedPG, selectedYear }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [view, setView] = useState('overview')

  useEffect(() => {
    // Generate dummy data
    const dummyData = getDummyComplaints()
    
    if (!selectedPGId) {
      setData(dummyData)
      setLoading(false)
      return
    }
    
    setLoading(true)
    setError(null)
    getAllComplaintsByPGID(selectedPGId)
      .then(response => {
        const raw = Array.isArray(response) ? response : (response?.data || [])
        if (raw && raw.length > 0) {
          const tagged = raw.map(c => ({
            ...c,
            _year: c.ComplaintDate ? String(new Date(c.ComplaintDate).getFullYear()) : '—',
            _month: c.ComplaintDate ? new Date(c.ComplaintDate).getMonth() : 0,
          }))
          setData(tagged)
        } else {
          setData(dummyData)
        }
      })
      .catch(() => {
        setData(dummyData)
      })
      .finally(() => setLoading(false))
  }, [selectedPGId])

  if (loading) return <div className="rpt-state">Loading complaints data…</div>
  if (error) return <div className="rpt-state rpt-state-error">Error: {error}</div>

  return (
    <div className="cp-rpt-root">
      <div className="cp-rpt-view-tabs">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'trends', label: 'Monthly trends' },
          { key: 'all', label: 'All complaints' },
        ].map(v => (
          <button
            key={v.key}
            className={`cp-rpt-vtab${view === v.key ? ' on' : ''}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && <CpOverview data={data} selectedYear={selectedYear} />}
      {view === 'trends' && <CpTrends data={data} />}
      {view === 'all' && <CpAllComplaints data={data} />}
    </div>
  )
}

export default OwnerComplaintsReport